import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  ChartLineUp, Bank, UsersThree, Package, Gear, FileText,
  SignOut, Sparkle, ShieldCheck, ShieldWarning,
  Wrench, ChartPie, Buildings, Megaphone, Stack, CheckCircle, Handshake, Crown,
  Scales, Calculator, CreditCard, TreeStructure, List as ListIcon, X, Book,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import api, { API_BASE } from "../lib/api";
import { applyDirForLang, startDomTranslator } from "../lib/i18n";
import CopilotPanel from "./Copilot";
import GlobalSearch from "./GlobalSearch";
import NotificationBell from "./NotificationBell";

const NAV = [
  { to: "/platform", label: "Platform (Owner)", icon: Crown, code: "★", testid: "nav-platform", module: null, superuserOnly: true },
  { to: "/", label: "Dashboard", icon: ChartLineUp, code: "00", testid: "nav-dashboard", module: null },
  { to: "/finance", label: "Finance", icon: Bank, code: "01", testid: "nav-finance", module: "finance" },
  { to: "/oman", label: "Oman Compliance", icon: Scales, code: "OM", testid: "nav-oman", module: "finance" },
  { to: "/ifrs", label: "IFRS Reports", icon: Calculator, code: "IF", testid: "nav-ifrs", module: "finance" },
  { to: "/banking", label: "Banking (H2H)", icon: CreditCard, code: "H2", testid: "nav-banking", module: "finance" },
  { to: "/aml", label: "AML Watchlist", icon: ShieldWarning, code: "AM", testid: "nav-aml", module: "finance" },
  { to: "/consolidation", label: "Consolidation", icon: TreeStructure, code: "CN", testid: "nav-consolidation", module: "finance" },
  { to: "/hr", label: "Human Resources", icon: UsersThree, code: "02", testid: "nav-hr", module: "hr", adminOnly: true },
  { to: "/hr/self-service", label: "My HR", icon: UsersThree, code: "SS", testid: "nav-hr-self", module: null, hideForAdmin: true },
  { to: "/scm", label: "Supply Chain", icon: Package, code: "03", testid: "nav-scm", module: "scm" },
  { to: "/hse", label: "Health & Safety", icon: ShieldWarning, code: "06", testid: "nav-hse", module: "hse" },
  { to: "/eam", label: "Assets", icon: Wrench, code: "07", testid: "nav-eam", module: "eam" },
  { to: "/analytics", label: "Analytics", icon: ChartPie, code: "08", testid: "nav-analytics", module: "analytics" },
  { to: "/org", label: "Organization", icon: Buildings, code: "09", testid: "nav-org", module: null, adminOnly: true },
  { to: "/workflows", label: "Workflows", icon: Stack, code: "11", testid: "nav-workflows", module: null, adminOnly: true },
  { to: "/approvals", label: "Approvals", icon: CheckCircle, code: "12", testid: "nav-approvals", module: null },
  { to: "/admin/leads", label: "Leads (Demo Org)", icon: Megaphone, code: "10", testid: "nav-admin-leads", module: null, adminOnly: true, defaultOrgOnly: true },
  { to: "/admin/affiliates", label: "Affiliates", icon: Handshake, code: "13", testid: "nav-affiliates", module: null, adminOnly: true, defaultOrgOnly: true },
  { to: "/audit", label: "Audit Log", icon: FileText, code: "04", testid: "nav-audit", module: null },
  { to: "/settings", label: "Settings", icon: Gear, code: "05", testid: "nav-settings", module: null },
];

function isNavItemVisible(item, user) {
  if (item.adminOnly && user?.role !== "admin" && user?.role !== "superuser") return false;
  if (item.superuserOnly && user?.role !== "superuser") return false;
  if (item.hideForAdmin && (user?.role === "admin" || user?.role === "superuser")) return false;
  if (item.defaultOrgOnly && user?.tenant?.tenant_id !== "tenant-default-org") return false;
  return true;
}

function SidebarNavItem({ item, locked }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      data-testid={item.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 transition-all ${
          isActive
            ? "border-[#FF3E00] text-zinc-950 bg-zinc-50"
            : `border-transparent ${locked ? "text-zinc-300" : "text-zinc-600"} hover:text-zinc-950 hover:bg-zinc-50`
        }`
      }
    >
      <span className="font-mono text-[10px] text-zinc-400 w-5">{item.code}</span>
      <Icon size={16} weight="regular" />
      <span className="flex-1">{item.label}</span>
      {locked && <span className="font-mono text-[9px] text-zinc-400">LOCK</span>}
    </NavLink>
  );
}

function SidebarUserPanel({ user, onLogout }) {
  return (
    <div className="border-t border-zinc-200 px-5 py-4">
      <Link to="/org" className="block mb-3 group" data-testid="sidebar-org-link">
        <div className="font-mono text-[9px] tracking-[0.15em] text-zinc-400">ORG</div>
        <div className="text-xs font-bold mt-0.5 group-hover:text-[#FF3E00] transition-colors truncate" data-testid="sidebar-org-name">
          {user?.tenant?.name || "—"}
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[#FF3E00] text-white" data-testid="sidebar-plan-badge">
            {user?.subscription?.plan?.toUpperCase() || "—"}
          </span>
        </div>
      </Link>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-zinc-950 text-white flex items-center justify-center font-display font-bold text-sm">
          {(user?.name || "U").slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
        </div>
        <button data-testid="logout-btn" onClick={onLogout} className="text-zinc-500 hover:text-[#D0021B] transition-colors" title="Sign out">
          <SignOut size={16} />
        </button>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[10px] font-mono tracking-wider text-zinc-500">
        <ShieldCheck size={12} weight={user?.mfa_enabled ? "fill" : "regular"} className={user?.mfa_enabled ? "text-emerald-600" : ""} />
        <span>{user?.mfa_enabled ? "MFA ACTIVE" : "MFA DISABLED"}</span>
      </div>
      <LangToggle user={user} />
    </div>
  );
}

function LangToggle({ user }) {
  const tenantLang = user?.tenant?.preferred_language === "ar" ? "ar" : "en";
  const currentLang = user?.effective_language || user?.preferred_language || tenantLang;
  const hasOverride = !!user?.preferred_language && user.preferred_language !== tenantLang;
  const canEditCompany = ["admin", "owner", "superuser"].includes(user?.role) || user?.tenant?.owner_email === user?.email;
  const setLang = async (lng) => {
    if (lng === currentLang) return;
    try {
      await api.patch("/auth/me/language", { language: lng });
      applyDirForLang(lng);
      toast.success(lng === "ar" ? "تم تفعيل اللغة العربية" : "Switched to English");
      window.location.reload();
    } catch { toast.error("Could not update language"); }
  };
  const setCompanyDefault = async () => {
    try {
      await api.patch("/tenant/language", { language: currentLang });
      await api.delete("/auth/me/language");
      toast.success(currentLang === "ar" ? "أصبحت العربية اللغة الافتراضية للشركة" : "English is now the company default");
      window.location.reload();
    } catch { toast.error("Could not update company language"); }
  };
  return (
    <div className="mt-2 space-y-1 text-[10px] font-mono" data-testid="sidebar-lang-toggle" data-no-i18n>
      <div className="flex items-center gap-1">
        <span className="text-zinc-400">LANG</span>
        <button onClick={() => setLang("en")} className={`px-1.5 py-0.5 border ${currentLang === "en" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-500 hover:border-zinc-950"}`} data-testid="lang-en-btn">EN</button>
        <button onClick={() => setLang("ar")} className={`px-1.5 py-0.5 border ${currentLang === "ar" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-500 hover:border-zinc-950"}`} data-testid="lang-ar-btn">عربي</button>
      </div>
      <div className="text-zinc-400 flex items-center gap-1 flex-wrap" data-testid="lang-scope-note">
        {hasOverride ? (currentLang === "ar" ? "لك فقط" : "just for you") : (currentLang === "ar" ? "افتراضي الشركة" : "company default")}
        {hasOverride && canEditCompany && (
          <button onClick={setCompanyDefault} className="underline hover:text-zinc-950" data-testid="lang-set-company-btn">{currentLang === "ar" ? "· اجعلها افتراضية للجميع" : "· make it company default"}</button>
        )}
      </div>
    </div>
  );
}

function TopBar({ moduleCode, moduleLabel, copilotOpen, onToggleCopilot, onToggleMobileNav }) {
  const { user } = useAuth();
  const isDemo = user && (user.is_demo || user.tenant_id === "tenant-demo" || (user.email || "").startsWith("demo"));
  return (
    <header className="h-14 border-b border-zinc-200 flex items-center justify-between px-4 md:px-6 bg-white/95 backdrop-blur">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onToggleMobileNav}
          data-testid="mobile-nav-toggle"
          className="md:hidden -ml-1 p-1.5 border border-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors"
          aria-label="Open navigation"
        >
          <ListIcon size={16} />
        </button>
        <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 hidden sm:inline">MODULE / {moduleCode}</span>
        <span className="font-display font-bold tracking-tight text-base truncate" data-testid="page-title">{moduleLabel}</span>
      </div>
      <div className="flex items-center gap-2">
        {isDemo && (
          <span
            className="font-mono text-[9px] tracking-[0.2em] uppercase bg-amber-400 text-zinc-950 border border-amber-500 px-2 py-1 leading-none hidden sm:inline-flex items-center gap-1"
            title="You're in the shared demo workspace. Data may be reset at any time."
            data-testid="demo-env-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-zinc-950 inline-block animate-pulse" />
            DEMO ENV
          </span>
        )}
        <GlobalSearch />
        <a
          href={`${API_BASE}/api/platform/docs/download/user-guide`}
          data-testid="download-user-guide-btn"
          title="Download the SmartERP User Guide (published by the platform owner)"
          className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-mono tracking-[0.15em] uppercase border border-zinc-300 text-zinc-600 hover:border-zinc-950 hover:text-zinc-950 transition-colors"
        >
          <Book size={12} weight="fill" /> USER GUIDE
        </a>
        <NotificationBell />
        <button
          data-testid="open-copilot-btn"
          onClick={onToggleCopilot}
          className={`flex items-center gap-2 px-2 md:px-3 py-1.5 text-xs font-semibold border transition-all ${
            copilotOpen
              ? "bg-zinc-950 text-white border-zinc-950"
              : "bg-white text-zinc-950 border-zinc-950 hover:bg-[#FF3E00] hover:text-white hover:border-[#FF3E00]"
          }`}
        >
          <Sparkle size={14} weight="fill" />
          <span className="hidden sm:inline">COPILOT</span>
        </button>
      </div>
    </header>
  );
}

function SidebarContent({ user, modules, onLogout, onClose }) {
  const visibleNav = useMemo(
    () => NAV.filter((item) => isNavItemVisible(item, user)),
    [user],
  );
  return (
    <>
      <div className="px-5 py-5 border-b border-zinc-200 flex items-center justify-between">
        <Link to="/" className="block group">
          <div className="font-mono text-[10px] tracking-[0.18em] text-zinc-400">SMARTERP / V1.0</div>
          <div className="font-display text-2xl font-black tracking-tight mt-0.5 group-hover:text-[#FF3E00] transition-colors">
            Enterprise.
          </div>
        </Link>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="md:hidden text-zinc-500 hover:text-zinc-950"
            data-testid="mobile-nav-close"
            aria-label="Close navigation"
          >
            <X size={20} />
          </button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        <div className="label-sm text-zinc-400 px-5 mb-2">Modules</div>
        {visibleNav.map((item) => (
          <SidebarNavItem
            key={item.to}
            item={item}
            locked={Boolean(item.module && !modules.includes(item.module))}
          />
        ))}
      </nav>
      <SidebarUserPanel user={user} onLogout={onLogout} />
    </>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const lang = user?.effective_language || user?.preferred_language || (user?.tenant?.preferred_language === "ar" ? "ar" : "en");
    applyDirForLang(lang);
    startDomTranslator(lang);
    return () => startDomTranslator("en");
  }, [user?.effective_language, user?.preferred_language, user?.tenant?.preferred_language]);

  // Close mobile nav on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const currentNav = NAV.find((n) => n.to === location.pathname);
  const moduleCode = currentNav?.code || "00";
  const moduleLabel = currentNav?.label || "Dashboard";
  const subscribed = user?.subscription?.modules || [];
  const effective = user?.effective_modules;
  // Prefer effective_modules (respects user override / custom role / department)
  // when the backend provides it, so a Finance-department user doesn't see
  // Supply Chain in the sidebar. Fall back to the tenant subscription list.
  const modules = Array.isArray(effective) && effective.length > 0 ? effective : subscribed;
  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-white text-zinc-950">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-[240px] flex-shrink-0 border-r border-zinc-200 flex-col" data-testid="app-sidebar">
        <SidebarContent user={user} modules={modules} onLogout={handleLogout} />
      </aside>

      {/* Mobile Sidebar Overlay + Drawer */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 z-[80] bg-zinc-950/40 backdrop-blur-sm"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-overlay"
        />
      )}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-[90] w-[260px] bg-white border-r border-zinc-200 flex flex-col transform transition-transform duration-200 ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar-mobile"
      >
        <SidebarContent user={user} modules={modules} onLogout={handleLogout} onClose={() => setMobileNavOpen(false)} />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar
          moduleCode={moduleCode}
          moduleLabel={moduleLabel}
          copilotOpen={copilotOpen}
          onToggleCopilot={() => setCopilotOpen((s) => !s)}
          onToggleMobileNav={() => setMobileNavOpen((s) => !s)}
        />
        <div className="flex-1 flex overflow-hidden">
          <main className="flex-1 overflow-y-auto" data-testid="main-content">
            <div className="animate-fade-up">
              <Outlet />
            </div>
          </main>
          {copilotOpen && <CopilotPanel onClose={() => setCopilotOpen(false)} />}
        </div>
      </div>
    </div>
  );
}
